import { canAccessPensionCalculators } from '../access';
import { ClientError, email, canReadClient, newClientData, savedClientData } from './clientModel';

// Injected SDK makes every read/write testable without connecting to a database.
export function createClientStore({ sdk, database, portal, auth, resolveRelatedEmails }) {
  const readableRecord = async ref => {
    try { return await sdk.getDoc(ref); }
    catch (error) { if (error.code === 'permission-denied') return null; throw error; }
  };
  async function access(session) {
    // Use the login contexts, just as Check2 does.
    if (!session?.userEmail) await auth.authStateReady?.();
    const user = auth.currentUser;
    const address = email(session?.userEmail || user?.email);
    if (!address) throw new ClientError(401, 'Your portal session is not available. Please sign in again.');
    const roles = { staff: 'Staff', advisers: 'Advisers', administrators: 'Administrators', admin: 'Administrators', admins: 'Administrators' };
    const contextRole = roles[String(session?.userRole || '').trim().toLowerCase()];
    if (session?.userRole && !contextRole) throw new ClientError(403, 'Your account does not have access to Pension Calculators.');
    if (contextRole) {
      const permissions = Array.isArray(session.userPermissions) ? session.userPermissions : [];
      if (!canAccessPensionCalculators(contextRole, permissions)) throw new ClientError(403, 'Your portal permissions do not allow access to Pension Calculators.');
      const related = contextRole === 'Staff' ? [address] : await resolveRelatedEmails?.(address, contextRole) || [address];
      const contextLinks = contextRole === 'Administrators' && Array.isArray(session.linkedAdviserEmails) ? session.linkedAdviserEmails : [];
      const relatedOwnerEmails = [...new Set([address, ...related, ...contextLinks].map(email).filter(Boolean))];
      return { uid: user?.uid || null, email: address, role: contextRole, userPermissions: permissions,
        adviserEmails: contextRole === 'Advisers' ? [address] : contextRole === 'Administrators' ? relatedOwnerEmails.filter(value => value !== address) : [], relatedOwnerEmails };
    }
    for (const role of contextRole ? [contextRole] : ['Staff', 'Advisers', 'Administrators']) {
      const record = await readableRecord(sdk.doc(portal, 'Authorized Members', role, 'Data', address));
      if (!record?.exists()) continue;
      const data = record.data();
      if (data.isAccessAllowed === false || data.isAccessAllowed === 'false' || (role === 'Advisers' && data.isAppointed === false && data.isAccessAllowed !== true)) throw new ClientError(403, 'Portal access is disabled.');
      let related = [];
      if (role !== 'Staff') {
        const field = role === 'Advisers' ? 'admins' : 'advisers';
        const other = role === 'Advisers' ? 'Administrators' : 'Advisers';
        related = Array.isArray(data[field]) ? data[field].map(email).filter(Boolean) : [];
        // As in Check2, use the existing canonical counterpart links.
        const records = await Promise.all([...new Set(related)].map(async value => {
          const linked = await readableRecord(sdk.doc(portal, 'Authorized Members', other, 'Data', value));
          return linked?.exists() && linked.data().isAccessAllowed !== false && linked.data().isAccessAllowed !== 'false' ? value : null;
        }));
        related = records.filter(Boolean);
      }
      return { uid: user?.uid || null, email: address, role, adviserEmails: role === 'Advisers' ? [address] : role === 'Administrators' ? related : [], relatedOwnerEmails: [address, ...related] };
    }
    throw new ClientError(403, 'Your account does not have access to Pension Calculators.');
  }
  const clientCollection = owner => sdk.collection(database, 'Pension Calculators', owner, 'Clients');
  const documentRef = (owner, id) => sdk.doc(database, 'Pension Calculators', owner, 'Clients', id);
  async function request(path, { method = 'GET', body, action, session } = {}) {
    const principal = await access(session);
    if (path === 'pension-client-access' && method === 'GET') return principal;
    const match = path.match(/^pension-clients(?:\/([^/]+)\/([A-Za-z0-9_-]{1,128}))?$/);
    if (!match) throw new ClientError(404, 'Client not found.');
    let owner;
    try { owner = match[1] ? decodeURIComponent(match[1]) : principal.email; }
    catch { throw new ClientError(400, 'Invalid client owner.'); }
    if (!/^[^/@\s]+@[^/\s]+$/.test(owner)) throw new ClientError(400, 'Invalid client owner.');
    const id = match[2];
    if (method === 'GET') {
      if (id) {
        const snapshot = await sdk.getDoc(documentRef(owner, id));
        if (!snapshot.exists() || snapshot.data().ownerEmail !== owner || !canReadClient(principal, snapshot.data())) throw new ClientError(404, 'Client not found or access is unavailable.');
        return { id, ...snapshot.data() };
      }
      const snapshots = principal.role === 'Staff'
        ? [await sdk.getDocs(sdk.collectionGroup(database, 'Clients'))]
        : await Promise.all(principal.relatedOwnerEmails.map(value => sdk.getDocs(clientCollection(value))));
      const unique = new Map();
      for (const snapshot of snapshots) for (const record of snapshot.docs) {
        if (!/^Pension Calculators\/[^/]+\/Clients\/[^/]+$/.test(record.ref.path)) continue;
        const value = record.data();
        if (canReadClient(principal, value)) unique.set(record.ref.path, { id: record.id, profile: value.profile, ownerEmail: value.ownerEmail, adviserEmail: value.adviserEmail, updatedAt: value.updatedAt, updatedBy: value.updatedBy, revision: value.revision, statuses: Object.fromEntries(Object.entries(value.calculators || {}).map(([key, draft]) => [key, draft.status])) });
      }
      const names = new Map(await Promise.all([...new Set([...unique.values()].map(client => client.adviserEmail || client.ownerEmail).filter(Boolean))].map(async address => {
        let data = {};
        for (const role of ['Advisers', 'Staff', 'Administrators']) {
          const record = await readableRecord(sdk.doc(portal, 'Authorized Members', role, 'Data', address, 'Personal Details', 'Data'));
          if (record?.exists()) { data = record.data(); break; }
        }
        const text = value => typeof value === 'string' ? value.trim() : typeof value?.value === 'string' ? value.value.trim() : '';
        const name = [text(data.forename || data.firstName || data['First Name']), text(data.surname || data.lastName || data['Last Name'])].filter(Boolean).join(' ') || text(data.fullName || data.name);
        return [address, name];
      })));
      return { clients: [...unique.values()].map(client => ({ ...client, adviserName: names.get(client.adviserEmail || client.ownerEmail) || '' })).sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt))) };
    }
    // The only write call sites: explicit Create client and Save progress handlers.
    if (method === 'POST' && !id && action === 'create-client') {
      const value = newClientData(principal, body, new Date().toISOString());
      const ref = sdk.doc(clientCollection(principal.email));
      await sdk.setDoc(ref, value);
      return { id: ref.id, ...value };
    }
    if (method === 'PUT' && id && action === 'save-progress') {
      const ref = documentRef(owner, id);
      const value = await sdk.runTransaction(database, async transaction => {
        const current = await transaction.get(ref);
        if (!current.exists() || current.data().ownerEmail !== owner) throw new ClientError(404, 'Client not found.');
        const next = savedClientData(principal, current.data(), body, new Date().toISOString());
        transaction.set(ref, next);
        return next;
      });
      return { id, ...value };
    }
    throw new ClientError(400, 'Use Create client or Save progress to save changes.');
  }
  return { request };
}
